const FILLS = [
  'var(--color-foreground)',
  'color-mix(in srgb, var(--color-foreground) 78%, var(--color-background))',
  'color-mix(in srgb, var(--color-primary) 82%, var(--color-foreground))',
  'color-mix(in srgb, var(--color-foreground) 62%, var(--color-background))',
  'color-mix(in srgb, var(--color-primary) 55%, var(--color-foreground))',
  'color-mix(in srgb, var(--color-foreground) 62%, var(--color-background))',
  'color-mix(in srgb, var(--color-foreground) 88%, var(--color-primary))',
  'color-mix(in srgb, var(--color-primary) 40%, var(--color-foreground))',
];

const MAX_SLICES = 8;
const OUTER = 46;
const INNER = 30;
const GAP = 2.4;

/**
 * Donut chart. A slice zooms into a topic. The center zooms back out.
 */
class BlogTopicChart extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;

    this.dataset.ready = 'true';
    this.#toggle = this.querySelector('.blog-topic-chart__toggle');
    this.#slices = this.querySelector('[data-slices]');
    this.#hub = this.querySelector('.blog-topic-chart__hub');
    this.#hubLabel = this.querySelector('[data-center]');
    this.#rootLabel = this.querySelector('[data-center-root]');
    this.#tip = this.querySelector('[data-tip]');
    this.#pageHandles = (this.dataset.pageHandles || '').split(',').filter(Boolean);
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#bind();

    const startingLevel = this.dataset.activeLevel || 'root';

    if (startingLevel !== 'root') {
      this.classList.add('is-open');
      this.#toggle?.setAttribute('aria-expanded', 'true');
    }

    this.#showLevel(startingLevel);

    const tagged = window.location.pathname.match(/\/tagged\/([^/]+)/);
    const handle = tagged?.[1];

    if (handle && handle === startingLevel) this.#openGroup(startingLevel);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const path = event.target.closest('path[data-zoom], path[data-href]');

    if (path?.dataset.zoom) {
      if (path.dataset.zoom === 'root') {
        this.#zoomOut();
        return;
      }

      this.#openGroup(path.dataset.zoom);
      return;
    }

    if (path?.dataset.href) {
      this.#go(path.dataset.href);
      return;
    }

    const link = event.target.closest('a[data-handle]');

    if (link) {
      const level = link.closest('[data-level]');

      if (level && link.dataset.handle === level.dataset.level) {
        event.preventDefault();
        this.#openGroup(level.dataset.level || 'root');
        return;
      }

      this.#go(link.href);
      return;
    }

    const zoom = event.target.closest('[data-zoom]');

    if (!zoom) return;

    event.preventDefault();

    if (zoom.dataset.zoom === 'root') {
      this.#zoomOut();
      return;
    }

    this.#openGroup(zoom.dataset.zoom || 'root');
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const path = event.target.closest('path[data-zoom], path[data-href]');

    if (!path) return;

    event.preventDefault();

    if (path.dataset.zoom) {
      if (path.dataset.zoom === 'root') {
        this.#zoomOut();
        return;
      }

      this.#openGroup(path.dataset.zoom);
      return;
    }

    if (path.dataset.href) this.#go(path.dataset.href);
  };

  #zoomOut() {
    if (this.#go(this.dataset.blogUrl)) return;

    const list = document.querySelector('.blog-posts-container');

    if (list && this.#originalList != null) list.innerHTML = this.#originalList;

    document.querySelector('.blog-posts__pagination')?.classList.remove('is-hidden');
    this.#setCount(this.#originalCount);
    const status = document.querySelector('[data-topic-status]');
    if (status) status.hidden = true;
    this.#showLevel('root');
  }

  /**
   * @param {string} levelId
   */
  #openGroup(levelId) {
    this.#showLevel(levelId);
    const level = this.querySelector(`[data-level="${CSS.escape(levelId)}"]`);
    if (level) this.#loadGroup(level);
  }

  /**
   * @param {HTMLElement} level
   */
  async #loadGroup(level) {
    const list = document.querySelector('.blog-posts-container');
    const sectionId = document.querySelector('blog-posts-list')?.getAttribute('section-id');

    if (!list || !sectionId) return;

    if (this.#originalList == null) {
      this.#originalList = list.innerHTML;
      const current = document.querySelector('[data-article-count]')?.textContent || '';
      const match = current.match(/\d+/);
      this.#originalCount = match ? Number(match[0]) : 0;
    }

    const request = (this.#requestId || 0) + 1;
    this.#requestId = request;
    list.setAttribute('aria-busy', 'true');

    const hrefs = await this.#groupHrefs(level);
    const batches = await Promise.all(hrefs.map((href) => this.#fetchTag(href, sectionId)));

    if (this.#requestId !== request) return;

    const seen = new Set();
    const items = [];

    for (const item of batches.flat()) {
      const href = item.querySelector('a[href*="/blogs/"]')?.getAttribute('href');
      if (!href || seen.has(href)) continue;
      seen.add(href);
      items.push(item);
    }

    list.replaceChildren(...items);
    list.setAttribute('aria-busy', 'false');
    document.querySelector('.blog-posts__pagination')?.classList.add('is-hidden');
    this.#setCount(items.length);

    const status = document.querySelector('[data-topic-status]');

    if (status) {
      const template = status.dataset.template || '__TOPIC__';
      status.hidden = false;
      status.textContent = template.replace('__TOPIC__', level.dataset.label || '');
    }

    if (items.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'blog-posts__empty';
      empty.textContent = list.dataset.empty || '';
      list.append(empty);
    }
  }

  /**
   * Every tag in the group, including the exact group tag and siblings
   * that only appear on the unfiltered blog.
   * @param {HTMLElement} level
   */
  async #groupHrefs(level) {
    const levelId = level.dataset.level || '';
    const hrefs = new Set();

    for (const link of level.querySelectorAll('a[href]')) {
      const href = link.getAttribute('href');
      if (href) hrefs.add(href);
    }

    const blogUrl = (this.dataset.blogUrl || '').replace(/\/$/, '');

    if (blogUrl && levelId) hrefs.add(`${blogUrl}/tagged/${levelId}`);

    const here = window.location.pathname.replace(/\/$/, '');
    const needsSiblings = hrefs.size <= 1 || here.endsWith(`/tagged/${levelId}`);

    if (needsSiblings && blogUrl && here !== blogUrl) {
      try {
        const response = await fetch(blogUrl);
        if (response.ok) {
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = doc.querySelector(`blog-topic-chart [data-level="${CSS.escape(levelId)}"]`);
          const list = level.querySelector('.blog-topic-chart__items');

          if (source && list && source.querySelectorAll('a[href]').length > list.querySelectorAll('a[href]').length) {
            list.replaceChildren(...source.querySelectorAll(':scope > li'));
            this.#draw(level);
          }

          source?.querySelectorAll('a[href]').forEach((link) => {
            const href = link.getAttribute('href');
            if (href) hrefs.add(href);
          });
        }
      } catch {
        /* keep the tags already on the page */
      }
    }

    return [...hrefs];
  }

  /**
   * @param {string} href
   * @param {string} sectionId
   */
  async #fetchTag(href, sectionId) {
    const items = [];
    let page = 1;
    let last = 1;

    while (page <= last && page <= 4) {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      if (page > 1) url.searchParams.set('page', String(page));

      try {
        const response = await fetch(url);
        if (!response.ok) break;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        items.push(...doc.querySelectorAll('.blog-post-item'));
        last = Number(doc.querySelector('.blog-posts-container')?.dataset.lastPage || 1);
      } catch {
        break;
      }

      page += 1;
    }

    return items;
  }

  /** @param {number} count */
  #setCount(count) {
    const element = document.querySelector('[data-article-count]');
    if (!element) return;

    if (count === 1 && element.dataset.countOne) {
      element.textContent = element.dataset.countOne;
      return;
    }

    const template = count < 20 && element.dataset.countFew ? element.dataset.countFew : element.dataset.countOther;
    element.textContent = (template || String(count)).replace(/\d+/, String(count));
  }

  /**
   * @param {string | undefined} href
   */
  #go(href) {
    if (!href) return false;

    const next = new URL(href, window.location.origin);
    const here = window.location.pathname.replace(/\/$/, '');
    const there = next.pathname.replace(/\/$/, '');

    if (here === there) return false;

    window.location.assign(next.href);
    return true;
  }

  #bind() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerout', this.#onPointerOut);
    this.addEventListener('focusin', this.#onPointerOver);
    this.addEventListener('focusout', this.#onPointerOut);
    this.#toggle?.addEventListener('click', () => {
      const open = this.classList.toggle('is-open');
      this.#toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /** @param {Event} event */
  #onPointerOver = (event) => {
    const path = event.target.closest?.('path[aria-label]');

    if (!path || !this.#tip) return;

    const label = path.getAttribute('aria-label') || '';
    this.#tip.hidden = false;
    this.#tip.textContent = label;
    const visual = this.querySelector('.blog-topic-chart__visual');
    const pathBox = path.getBoundingClientRect();
    const visualBox = visual?.getBoundingClientRect();

    if (!visualBox) return;

    this.#tip.style.left = `${pathBox.left + pathBox.width / 2 - visualBox.left}px`;
    this.#tip.style.top = `${pathBox.top - visualBox.top}px`;
  };

  /** @param {Event} event */
  #onPointerOut = (event) => {
    const path = event.target.closest?.('path[aria-label]');
    const next = event.relatedTarget;

    if (!path || (next && path.contains(next))) return;
    if (this.#tip) this.#tip.hidden = true;
  };

  /**
   * @param {string} levelId
   */
  #showLevel(levelId) {
    const next = this.querySelector(`[data-level="${CSS.escape(levelId)}"]`);
    const level = next || this.querySelector('[data-level="root"]');

    if (!level) return;

    const levelName = level.dataset.level || 'root';
    const zoomed = levelName !== 'root';

    for (const panel of this.querySelectorAll('.blog-topic-chart__level')) {
      panel.hidden = panel !== level;
    }

    for (const button of this.querySelectorAll('button[data-zoom]')) {
      if (button.dataset.zoom === 'root') continue;
      button.setAttribute('aria-expanded', button.dataset.zoom === levelName ? 'true' : 'false');
    }

    if (this.#hub) this.#hub.hidden = !zoomed;
    if (this.#rootLabel) this.#rootLabel.hidden = zoomed;
    if (this.#hubLabel) this.#hubLabel.textContent = level.dataset.label || '';

    if (!this.#reducedMotion) {
      this.classList.add('is-zooming');
      window.setTimeout(() => this.classList.remove('is-zooming'), 360);
    }

    this.#draw(level);
  }

  /**
   * @param {HTMLElement} level
   */
  #draw(level) {
    if (!this.#slices) return;

    const isRoot = level.dataset.level === 'root';
    const entries = [];

    for (const node of level.querySelectorAll('button[data-zoom], a[data-handle]')) {
      if (node.dataset.zoom === 'root') continue;

      const handle = node.dataset.handle || '';
      const zoom = node.dataset.zoom || '';
      let pageHits = 0;
      let weight = 1;

      if (handle) {
        pageHits = this.#pageHandles.filter((item) => item === handle).length;
        weight = Math.max(pageHits, 1);
      }

      if (zoom) {
        const childLevel = this.querySelector(`[data-level="${CSS.escape(zoom)}"]`);
        pageHits = 0;
        weight = 0;

        if (childLevel) {
          for (const link of childLevel.querySelectorAll('a[data-handle]')) {
            const hits = this.#pageHandles.filter((item) => item === (link.dataset.handle || '')).length;
            pageHits += hits;
            weight += Math.max(hits, 1);
          }
        }
      }

      const count = node.querySelector('.blog-topic-chart__count');
      if (count) count.textContent = pageHits > 0 ? String(pageHits) : '';

      entries.push({
        label: node.querySelector('.blog-topic-chart__name')?.textContent?.trim() || '',
        weight,
        zoom,
        href: node.getAttribute('href') || '',
        node,
      });
    }

    const chartEntries = [...entries].sort((a, b) => b.weight - a.weight).slice(0, isRoot ? entries.length : MAX_SLICES);
    const total = chartEntries.reduce((sum, entry) => sum + entry.weight, 0) || 1;
    let angle = 0;

    this.#slices.replaceChildren();

    for (const [index, entry] of chartEntries.entries()) {
      const sweep = (entry.weight / total) * 360;
      const fill = FILLS[index % FILLS.length];
      const swatch = entry.node.querySelector('[data-swatch]');
      if (swatch) swatch.style.background = fill;

      const pad = sweep > GAP * 2.2 ? GAP : 0;
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', this.#slicePath(angle + pad / 2, angle + sweep - pad / 2));
      path.setAttribute('fill', fill);
      path.setAttribute('aria-label', entry.label);
      path.setAttribute('role', 'button');
      path.tabIndex = 0;
      const mid = angle + sweep / 2;
      const [pullX, pullY] = this.#point(0, 0, 2.2, mid);
      path.style.setProperty('--pull-x', `${pullX}px`);
      path.style.setProperty('--pull-y', `${pullY}px`);

      if (isRoot && entry.zoom) {
        path.dataset.zoom = entry.zoom;
        path.dataset.href = entry.node.dataset.href || '';
      }
      if (!isRoot && entry.href) {
        path.dataset.href = entry.href;
        path.dataset.handle = entry.node.dataset.handle || '';
      }

      this.#slices.append(path);
      angle += sweep;
    }
  }

  /**
   * @param {number} start
   * @param {number} end
   */
  #slicePath(start, end) {
    const sweep = Math.max(end - start, 0.4);
    const finish = start + sweep;
    const large = sweep > 180 ? 1 : 0;
    const [x1, y1] = this.#point(50, 50, OUTER, start);
    const [x2, y2] = this.#point(50, 50, OUTER, finish);
    const [x3, y3] = this.#point(50, 50, INNER, finish);
    const [x4, y4] = this.#point(50, 50, INNER, start);

    return `M ${x1} ${y1} A ${OUTER} ${OUTER} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${INNER} ${INNER} 0 ${large} 0 ${x4} ${y4} Z`;
  }

  /**
   * @param {number} cx
   * @param {number} cy
   * @param {number} radius
   * @param {number} angle
   */
  #point(cx, cy, radius, angle) {
    const radians = ((angle - 90) * Math.PI) / 180;

    return [
      Math.round((cx + radius * Math.cos(radians)) * 100) / 100,
      Math.round((cy + radius * Math.sin(radians)) * 100) / 100,
    ];
  }

  /** @type {HTMLButtonElement | null} */
  #toggle = null;

  /** @type {SVGGElement | null} */
  #slices = null;

  /** @type {HTMLButtonElement | null} */
  #hub = null;

  /** @type {HTMLElement | null} */
  #hubLabel = null;

  /** @type {HTMLElement | null} */
  #rootLabel = null;

  /** @type {HTMLElement | null} */
  #tip = null;

  /** @type {string[]} */
  #pageHandles = [];

  /** @type {string | null} */
  #originalList = null;

  /** @type {number} */
  #originalCount = 0;

  /** @type {number} */
  #requestId = 0;

  /** @type {boolean} */
  #reducedMotion = false;
}

if (!customElements.get('blog-topic-chart')) {
  customElements.define('blog-topic-chart', BlogTopicChart);
}
